import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAuth } from 'firebase/auth';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../../firebase';
import { initializeHousehold, joinHouseholdWithCode } from '../../features/household-engine/components/householdService';
import { AppState, AppAction, Household } from '@/types';
import {
  Home, Users, ArrowRight, Check, Copy, CheckCircle2, UserPlus, Loader2,
} from 'lucide-react';

const RECAPTCHA_SITE_KEY = '6LcVLIYsAAAAADaw3VMLLs4jXulXVwzwsp3qdex6';

async function getRecaptchaToken(action: string): Promise<string> {
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { TemplateSelector } from './TemplateSelector';
import { useOnboarding } from '../../context/OnboardingContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type WizardMode = 'choose' | 'creator' | 'joiner';

interface WizardProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  pendingInviteCode?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function generateAndSaveInviteCode(householdId: string, householdName: string, createdBy: string): Promise<string> {
  const code = Math.random().toString(36).substring(2, 8).toUpperCase();
  const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  const now = new Date().toISOString();
  await setDoc(doc(db, 'invites', code), {
    code, householdId, householdName, expiresAt, createdBy, createdAt: now,
  });
  await updateDoc(doc(db, 'households', householdId), {
    activeInvite: { code, expiresAt, createdBy, createdAt: now },
  });
  return code;
}

// ─── Step indicator ────────────────────────────────────────────────────────────

const StepDots: React.FC<{ total: number; current: number }> = ({ total, current }) => (
  <div className="flex gap-1.5 mb-8">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
          i < current ? 'bg-primary-500' : i === current ? 'bg-slate-400' : 'bg-slate-700'
        }`}
      />
    ))}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

export const HouseholdSetupWizard: React.FC<WizardProps> = ({ state, dispatch, pendingInviteCode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { markStep, setUserType } = useOnboarding();

  // wizard mode & step
  const [mode, setMode] = useState<WizardMode>(pendingInviteCode ? 'joiner' : 'choose');
  const [creatorStep, setCreatorStep] = useState(1);
  const [joinerStep, setJoinerStep] = useState(1);

  // creator state
  const [householdName, setHouseholdName] = useState('');
  const [householdType, setHouseholdType] = useState('family');
  const [isCreating, setIsCreating] = useState(false);
  const [createdHousehold, setCreatedHousehold] = useState<Household | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteCodeFailed, setInviteCodeFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creatorError, setCreatorError] = useState<string | null>(null);

  // plan step state
  const [isUpgradingPlan, setIsUpgradingPlan] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  const handleWizardUpgrade = async (priceId: string) => {
    if (!createdHousehold?.id) return;
    setIsUpgradingPlan(true);
    setPlanError(null);
    try {
      const recaptchaToken = await getRecaptchaToken('checkout');
      const fns = getFunctions(getApp());
      const createSession = httpsCallable(fns, 'createCheckoutSession');
      const result = await createSession({ priceId, householdId: createdHousehold.id, recaptchaToken });
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      setPlanError(t('wizard.plan_checkout_error', 'Failed to start checkout. Please try again.'));
      setIsUpgradingPlan(false);
    }
  };

  // joiner state
  const [joinerCode, setJoinerCode] = useState(pendingInviteCode ?? '');
  const [isJoining, setIsJoining] = useState(false);
  const [joinedHousehold, setJoinedHousehold] = useState<Household | null>(null);
  const [joinerError, setJoinerError] = useState<string | null>(null);
  const [autoJoinFailed, setAutoJoinFailed] = useState(false);

  // Auto-create household when entering creator step 2
  useEffect(() => {
    if (mode !== 'creator' || creatorStep !== 2 || createdHousehold || isCreating) return;
    const user = getAuth().currentUser;
    if (!user) { setCreatorError(t('wizard.not_signed_in', 'Not signed in')); return; }

    setIsCreating(true);
    setCreatorError(null);

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(t('wizard.err_timeout', 'Setup timed out. Check your connection and try again.'))), 15000)
    );

    Promise.race([initializeHousehold(user, householdName), timeout])
      .then(async (h: Household) => {
        setCreatedHousehold(h);
        dispatch({ type: 'ADD_HOUSEHOLD', payload: h });
        dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: h.id });
        // Generate and display invite code
        try {
          const code = await generateAndSaveInviteCode(h.id, h.name, user.uid);
          setInviteCode(code);
        } catch {
          // non-critical — household still created; flag so UI shows retry option
          setInviteCodeFailed(true);
        }
        // Mark step in onboarding state
        await setUserType('creator');
        await markStep('createHousehold');
      })
      .catch((e) => setCreatorError(e.message))
      .finally(() => setIsCreating(false));
  }, [mode, creatorStep, createdHousehold, isCreating]);

  // Auto-join when entering joiner step 1 (pre-filled from URL)
  useEffect(() => {
    if (mode !== 'joiner' || joinerStep !== 1 || !pendingInviteCode || joinedHousehold || isJoining) return;
    handleJoin(pendingInviteCode, true);
  }, [mode]);

  const handleJoin = async (codeToUse?: string, fromDeepLink = false) => {
    const code = (codeToUse ?? joinerCode).toUpperCase().trim();
    if (!code) return;
    const user = getAuth().currentUser;
    if (!user) { setJoinerError(t('wizard.not_signed_in', 'Not signed in')); return; }

    setIsJoining(true);
    setJoinerError(null);
    setAutoJoinFailed(false);
    try {
      const h = await joinHouseholdWithCode(user, code);
      setJoinedHousehold(h);
      dispatch({ type: 'ADD_HOUSEHOLD', payload: h });
      dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: h.id });
      await setUserType('joiner');
      await markStep('joinHousehold');
      setJoinerStep(2);
    } catch (e: any) {
      const msg = e.message ?? '';
      if (msg.includes('expired') || msg.includes('Invalid') || msg.includes('non-existent')) {
        setJoinerError(t('wizard.joiner.err_expired_code', 'This invite code is invalid or has expired. Please ask for a new one.'));
      } else if (msg.includes('already a member')) {
        setJoinerError(t('wizard.joiner.err_already_member', 'You are already a member of this household.'));
      } else {
        setJoinerError(msg || t('wizard.joiner.err_generic', 'Could not join. Please try again.'));
      }
      if (fromDeepLink) {
        setAutoJoinFailed(true);
        setJoinerCode(''); // Clear the expired code so user can enter a fresh one
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleCopyCode = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Path selection ──────────────────────────────────────────────────────────

  if (mode === 'choose') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 rounded-2xl p-8 w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-white">
              {t('wizard.choose_path.title', 'Welcome to EverydayOS')}
            </h2>
            <p className="text-slate-400 text-sm">
              {t('wizard.choose_path.subtitle', 'Are you starting a new household or joining one?')}
            </p>
          </div>

          <div className="space-y-3 pt-2">
            <button
              onClick={() => setMode('creator')}
              className="w-full flex items-center gap-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-primary-500 text-white rounded-xl p-4 transition-all text-left"
            >
              <div className="p-2.5 bg-primary-500/10 rounded-xl shrink-0">
                <Home className="w-5 h-5 text-primary-400" />
              </div>
              <div>
                <p className="font-semibold">{t('wizard.choose_path.create_label', 'Create a new household')}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {t('wizard.choose_path.create_hint', "You'll be the owner and can invite members")}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 ml-auto shrink-0" />
            </button>

            <button
              onClick={() => setMode('joiner')}
              className="w-full flex items-center gap-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-primary-500 text-white rounded-xl p-4 transition-all text-left"
            >
              <div className="p-2.5 bg-emerald-500/10 rounded-xl shrink-0">
                <UserPlus className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold">{t('wizard.choose_path.join_label', 'Join with an invite code')}</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {t('wizard.choose_path.join_hint', 'Someone shared a code with you')}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 ml-auto shrink-0" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Joiner flow ─────────────────────────────────────────────────────────────

  if (mode === 'joiner') {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 rounded-2xl p-8 w-full max-w-md space-y-6">
          <StepDots total={2} current={joinerStep - 1} />

          {joinerStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white">
                  {t('wizard.joiner.enter_code_title', 'Enter your invite code')}
                </h2>
                <p className="text-slate-400 mt-2 text-sm">
                  {t('wizard.joiner.enter_code_subtitle', 'Your household admin shared a 6-character code with you.')}
                </p>
              </div>

              <input
                type="text"
                value={joinerCode}
                onChange={(e) => setJoinerCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="w-full bg-slate-800 text-white text-center text-2xl font-mono tracking-[0.5em] rounded-lg px-4 py-4 outline-none focus:ring-2 focus:ring-primary-500 uppercase"
                placeholder="XXXXXX"
                autoFocus
              />

              {joinerError && (
                <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm space-y-1">
                  <p>{joinerError}</p>
                  {autoJoinFailed && (
                    <p className="text-red-400 text-xs">
                      {t('wizard.joiner.err_deeplink_hint', 'Enter a valid code below, or go back to start fresh.')}
                    </p>
                  )}
                </div>
              )}

              <button
                onClick={() => handleJoin()}
                disabled={joinerCode.trim().length < 4 || isJoining}
                className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {isJoining
                  ? <><LoadingSpinner size="sm" /> {t('wizard.joiner.joining', 'Joining…')}</>
                  : <>{t('wizard.joiner.join_button', 'Join Household')} <ArrowRight className="w-4 h-4" /></>
                }
              </button>

              <button
                onClick={() => setMode('choose')}
                className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                ← {t('wizard.back', 'Back')}
              </button>
            </div>
          )}

          {joinerStep === 2 && joinedHousehold && (
            <div className="space-y-6 text-center">
              <div className="flex justify-center">
                <div className="p-4 bg-emerald-500/10 rounded-2xl">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {t('wizard.joiner.welcome_title', 'Welcome!')}
                </h2>
                <p className="text-emerald-400 text-lg font-semibold mt-1">{joinedHousehold.name}</p>
                <p className="text-slate-400 text-sm mt-2">
                  {t('wizard.joiner.welcome_subtitle', "You're now part of the household. Start exploring!")}
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {t('wizard.go_to_dashboard', 'Go to Dashboard')} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Creator flow ────────────────────────────────────────────────────────────

  const CREATOR_TOTAL_STEPS = 5;

  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl p-8 w-full max-w-md space-y-6">
        <StepDots total={CREATOR_TOTAL_STEPS} current={creatorStep - 1} />

        {/* Step 1: Name + Type */}
        {creatorStep === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">{t('wizard.create_title', 'Create your household')}</h2>
              <p className="text-slate-400 mt-2 text-sm">{t('wizard.create_subtitle', 'Give your household a name to get started.')}</p>
            </div>

            <input
              type="text"
              value={householdName}
              onChange={(e) => setHouseholdName(e.target.value)}
              className="w-full bg-slate-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={t('wizard.name_placeholder', 'e.g. The Johnson Family')}
              autoFocus
            />

            <select
              value={householdType}
              onChange={(e) => setHouseholdType(e.target.value)}
              className="w-full bg-slate-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="family">{t('wizard.type_family', 'Family')}</option>
              <option value="roommates">{t('wizard.type_roommates', 'Roommates')}</option>
              <option value="couple">{t('wizard.type_couple', 'Couple')}</option>
              <option value="shared_property">{t('wizard.type_shared_property', 'Shared Property')}</option>
              <option value="other">{t('wizard.type_other', 'Other')}</option>
            </select>

            <button
              onClick={() => setCreatorStep(2)}
              disabled={!householdName.trim()}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {t('wizard.continue', 'Continue')} <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => setMode('choose')}
              className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors"
            >
              ← {t('wizard.back', 'Back')}
            </button>
          </div>
        )}

        {/* Step 2: Creating + Invite Code */}
        {creatorStep === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">
                {isCreating
                  ? t('wizard.setting_up', 'Setting up your household…')
                  : t('wizard.household_created', 'Household created!')}
              </h2>
            </div>

            {creatorError && (
              <div className="bg-red-900/50 text-red-300 px-4 py-2 rounded-lg text-sm">
                {creatorError}
                <button
                  onClick={() => { setCreatorError(null); setCreatedHousehold(null); setCreatorStep(1); }}
                  className="ml-2 underline"
                >
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            )}

            {(isCreating || (!createdHousehold && !creatorError)) ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : createdHousehold ? (
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-4 text-center">
                  <p className="text-emerald-400 font-semibold flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" /> {createdHousehold.name}
                  </p>
                </div>

                {/* Invite code display */}
                <div className="bg-slate-800 rounded-xl p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-400" />
                    <p className="text-sm font-semibold text-white">
                      {t('wizard.creator.invite_code_label', 'Share this code to invite members')}
                    </p>
                  </div>
                  {inviteCode ? (
                    <>
                      <p className="text-3xl font-mono font-black tracking-[0.4em] text-center text-primary-300 py-2">
                        {inviteCode}
                      </p>
                      <button
                        onClick={handleCopyCode}
                        className="w-full flex items-center justify-center gap-2 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        {copied
                          ? <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t('wizard.creator.copied', 'Copied!')}</>
                          : <><Copy className="w-4 h-4" /> {t('wizard.creator.copy_code', 'Copy code')}</>
                        }
                      </button>
                      <p className="text-2xs text-slate-400 text-center">
                        {t('wizard.creator.code_expires', 'Code expires in 24 hours')}
                      </p>
                    </>
                  ) : inviteCodeFailed ? (
                    <div className="text-center space-y-2">
                      <p className="text-sm text-slate-500">{t('wizard.creator.code_failed', 'Could not generate invite code.')}</p>
                      <button
                        onClick={async () => {
                          if (!createdHousehold) return;
                          const user = getAuth().currentUser;
                          if (!user) return;
                          setInviteCodeFailed(false);
                          try {
                            const code = await generateAndSaveInviteCode(createdHousehold.id, createdHousehold.name, user.uid);
                            setInviteCode(code);
                          } catch { setInviteCodeFailed(true); }
                        }}
                        className="text-primary-400 text-sm underline"
                      >
                        {t('common.retry', 'Retry')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-center py-2"><LoadingSpinner size="sm" /></div>
                  )}
                </div>

                <button
                  onClick={() => setCreatorStep(3)}
                  disabled={isCreating}
                  className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  {t('wizard.continue', 'Continue')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Step 3: Plan */}
        {creatorStep === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">{t('wizard.choose_plan', 'Choose your plan')}</h2>
              <p className="text-slate-400 mt-2 text-sm">{t('wizard.upgrade_anytime', 'You can upgrade anytime.')}</p>
            </div>

            {planError && (
              <p className="text-sm text-red-400 text-center bg-red-900/30 rounded-lg py-2 px-3">{planError}</p>
            )}

            <div className="space-y-3">
              {/* Free */}
              <div className="bg-slate-800 rounded-xl p-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-semibold">{t('wizard.plan_free_name', 'Free')}</span>
                  <span className="text-slate-400 text-sm">$0 / month</span>
                </div>
                <p className="text-slate-400 text-xs mb-3">Up to 20 tasks · Calendar · 2 members</p>
                <button
                  onClick={() => setCreatorStep(4)}
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 rounded-lg text-sm transition-colors"
                >
                  {t('wizard.continue_free', 'Continue with Free')} →
                </button>
              </div>

              {/* Basic */}
              <div className="bg-slate-800 rounded-xl p-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-semibold">{t('wizard.plan_basic_name', 'Basic')}</span>
                  <span className="text-slate-400 text-sm">$4.99 / month</span>
                </div>
                <p className="text-slate-400 text-xs mb-3">500 tasks · 6 members · All core modules</p>
                <button
                  onClick={() => handleWizardUpgrade((import.meta as any).env?.VITE_STRIPE_BASIC_PRICE_ID ?? '')}
                  disabled={isUpgradingPlan}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {isUpgradingPlan ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {t('wizard.choose_basic', 'Choose Basic')} →
                </button>
              </div>

              {/* Premium */}
              <div className="bg-slate-800 border border-amber-500/40 rounded-xl p-4 relative">
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest rounded">
                  Most Popular
                </div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-semibold">{t('wizard.plan_premium_name', 'Premium')}</span>
                  <span className="text-amber-400 text-sm font-bold">$9.99 / month</span>
                </div>
                <p className="text-slate-400 text-xs mb-3">5,000 tasks · 12 members · AI Assistant · All modules</p>
                <button
                  onClick={() => handleWizardUpgrade((import.meta as any).env?.VITE_STRIPE_PREMIUM_PRICE_ID ?? '')}
                  disabled={isUpgradingPlan}
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {isUpgradingPlan ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {t('wizard.choose_premium', 'Choose Premium')} →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Template */}
        {creatorStep === 4 && createdHousehold && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">
                {t('wizard.creator.template_title', 'Start with a template')}
              </h2>
              <p className="text-slate-400 mt-2 text-sm">
                {t('wizard.creator.template_subtitle', 'Pre-populate your household with tasks and categories that match your lifestyle.')}
              </p>
            </div>

            <TemplateSelector
              householdId={createdHousehold.id}
              userTier={state.currentUser?.subscription?.tier}
              onApplied={() => setCreatorStep(5)}
              onSkip={() => setCreatorStep(5)}
            />
          </div>
        )}

        {/* Step 5: Done */}
        {creatorStep === 5 && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <div className="p-4 bg-primary-500/10 rounded-2xl">
                <CheckCircle2 className="w-10 h-10 text-primary-400" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">{t('wizard.all_set', "You're all set!")}</h2>
              <p className="text-primary-400 text-lg font-semibold">{createdHousehold?.name ?? householdName}</p>
            </div>

            <div className="flex justify-around py-2">
              {[
                { label: t('nav.tasks', 'Tasks'), emoji: '✅' },
                { label: t('nav.calendar', 'Calendar'), emoji: '📅' },
                { label: t('nav.grocery', 'Grocery'), emoji: '🛒' },
              ].map(({ label, emoji }) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <span className="text-2xl">{emoji}</span>
                  <span className="text-slate-400 text-xs">{label}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {t('wizard.go_to_dashboard', 'Go to Dashboard')} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
